import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { messagesAPI } from '../../services/api';
import { Message, Conversation } from '../../types';
import toast from 'react-hot-toast';

interface MessagesState {
  conversations: Conversation[];
  messages: Message[];
  currentConversation: Conversation | null;
  loading: boolean;
  sendingMessage: boolean;
  error: string | null;
  loadingMessages: boolean;
  currentPage: number;
  hasMore: boolean;
  unreadCount: number;
  conversationsPage: number;
  hasMoreConversations: boolean;
  messageRequests: Conversation[];
  loadingRequests: boolean;
}

const initialState: MessagesState = {
    conversations: [],
    messages: [],
    messageRequests: [],
    loading: false,
    loadingMessages: false,
    loadingRequests: false,
    error: null,
    currentConversation: null,
    sendingMessage: false,
    currentPage: 1,
    hasMore: true,
    unreadCount: 0,
    conversationsPage: 1,
    hasMoreConversations: true
};

// Async thunks
export const fetchConversations = createAsyncThunk(
  'messages/fetchConversations',
  async ({ page = 1, collabFilter }: { page?: number; collabFilter?: string } = {}, { rejectWithValue }) => {
    try {
      const response = await messagesAPI.getConversations(page, 20, collabFilter);
      // console.log(response)
      return {
        conversations: response.data.data == null ? [] : response.data.data,
        page
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch conversations');
    }
  }
);

export const fetchMessageRequests = createAsyncThunk(
  'messages/fetchMessageRequests',
  async ({ page = 1 }: { page?: number } = {}, { rejectWithValue }) => {
    try {
      const response = await messagesAPI.getMessageRequests(page);
      return {
        requests: response.data.data == null ? [] : response.data.data,
        page
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch message requests');
    }
  }
);

export const handleMessageRequest = createAsyncThunk(
  'messages/handleMessageRequest',
  async ({ conversationId, action }: { conversationId: string; action: 'accept' | 'reject' }, { rejectWithValue }) => {
    try {
      await messagesAPI.handleMessageRequest(conversationId, action);
      return { conversationId, action };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to handle message request');
    }
  }
);

export const fetchMessages = createAsyncThunk(
  'messages/fetchMessages',
  async ({ conversation_id, page = 1 }: { conversation_id: string; page?: number }, { rejectWithValue }) => {
    try {
      const response = await messagesAPI.getMessages(conversation_id, page);
      // console.log("response", response)
      return {
        messages: response.data.data.reverse(),
        conversation_id,
        page,
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch messages');
    }
  }
);

export const sendMessage = createAsyncThunk(
  'messages/sendMessage',
  async (messageData: { recipient_id: string; content: string; message_type?: string }, { rejectWithValue }) => {
    try {
      const response = await messagesAPI.sendMessage(messageData);
      // console.log(response)
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to send message');
    }
  }
);

export const markAsRead = createAsyncThunk(
  'messages/markAsRead',
  async (conversationId: string, { rejectWithValue }) => {
    try {
      await messagesAPI.markAsRead(conversationId);
      return conversationId;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to mark messages as read');
    }
  }
);

export const fetchUnreadMessageCount = createAsyncThunk(
  'messages/fetchUnreadCount',
  async (_, { rejectWithValue }) => {
    try {
      const response = await messagesAPI.getUnreadCount();
      return response.data.data.count;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch unread count');
    }
  }
);

export const deleteConversation = createAsyncThunk(
  'messages/deleteConversation',
  async (conversationId: string, { rejectWithValue }) => {
    try {
      await messagesAPI.deleteConversation(conversationId);
      return conversationId;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to delete conversation');
    }
  }
);

const messagesSlice = createSlice({
  name: 'messages',
  initialState,
  reducers: {
    setCurrentConversation: (state, action: PayloadAction<Conversation>) => {
      state.currentConversation = action.payload;
    },
    clearCurrentConversation: (state) => {
      state.currentConversation = null;
      state.messages = [];
    },
   // Add message in real-time (from WebSocket)
//    addMessage: (state, action: PayloadAction<Message>) => {
//     const newMessage = action.payload;
    
//     // Add to current messages if it's for the active conversation
//     if (state.currentConversation && 
//         (newMessage.conversation_id === state.currentConversation.conversation_id ||
//          newMessage.conversation_id === state.currentConversation.conversation_id)) {
//       // Check if message already exists to avoid duplicates
//       const exists = state.messages.some(msg => msg.id === newMessage.id);
//       if (!exists) {
//         state.messages.push(newMessage);
//       }
//     }
    
//     // Update conversation list with new last message
//     const conversationIndex = state.conversations.findIndex(conv => 
//       conv.conversation_id === newMessage.conversation_id
//     );
    
//     if (conversationIndex !== -1) {
//       state.conversations[conversationIndex] = {
//         ...state.conversations[conversationIndex],
//         last_message: newMessage.content,
//         last_message_at: newMessage.created_at,
//       };
      
//       // Move conversation to top
//       const conversation = state.conversations[conversationIndex];
//       state.conversations.splice(conversationIndex, 1);
//       state.conversations.unshift(conversation);
//     }
//   },
  
  // Set active conversation
  setcurrentConversation: (state, action: PayloadAction<Conversation | null>) => {
    state.currentConversation = action.payload;
  },
  
  // Update message status (read/delivered)
//   updateMessageStatus: (state, action: PayloadAction<{ messageId: string; status: any }>) => {
//     const messageIndex = state.messages.findIndex(msg => msg.id === action.payload.messageId);
//     if (messageIndex !== -1) {
//       state.messages[messageIndex] = {
//         ...state.messages[messageIndex],
//         ...action.payload.status
//       };
//     }
//   },
//

  addMessage: (state, action: PayloadAction<Message>) => {
    const newMessage = action.payload;
    
    // Add to current messages if it's for the active conversation
    if (state.currentConversation && 
        (newMessage.conversation_id === state.currentConversation.conversation_id ||
         newMessage.conversation_id === state.currentConversation.conversation_id)) {
      // Check if message already exists to avoid duplicates
      const exists = state.messages.some(msg => msg.id === newMessage.id);
      if (!exists) {
        state.messages.push(newMessage);
      }
    }
    
    // Update conversation list with new last message
    const conversationIndex = state.conversations.findIndex(conv => 
      conv.conversation_id === newMessage.conversation_id
    );
    
    if (conversationIndex !== -1) {
      // Check if we are currently viewing this conversation
      const isViewing = state.currentConversation && 
                        state.currentConversation.conversation_id === newMessage.conversation_id;

      state.conversations[conversationIndex] = {
        ...state.conversations[conversationIndex],
        last_message: newMessage.content,
        last_message_at: newMessage.created_at,
        // Only increment unread count if we are NOT viewing the conversation
        unread_count: isViewing 
          ? (state.conversations[conversationIndex].unread_count || 0) 
          : (state.conversations[conversationIndex].unread_count || 0) + 1
      };
      
      // Move conversation to top
      const conversation = state.conversations[conversationIndex];
      state.conversations.splice(conversationIndex, 1);
      state.conversations.unshift(conversation);
    } else {
      // New conversation!
      // We assume newMessage.sender is the other user (since we received this via WS as recipient)
      if (newMessage.sender) {
        const newConversation: Conversation = {
          conversation_id: newMessage.conversation_id,
          last_message: newMessage.content,
          last_message_at: newMessage.created_at,
          unread_count: 1,
          other_user: newMessage.sender
        };
        state.conversations.unshift(newConversation);
      }
    }
    
    // Increment global unread count if message is not from current user and not in current conversation
    if (!state.currentConversation || state.currentConversation.conversation_id !== newMessage.conversation_id) {
        state.unreadCount = (state.unreadCount || 0) + 1;
    }
  },
  
  // Set active conversation
//   setActiveConversation: (state, action: PayloadAction<Conversation | null>) => {
//     state.currentConversation = action.payload;
//   },
  
  // Update message status (read/delivered)
  updateMessageStatus: (state, action: PayloadAction<{ messageId: string; status: any }>) => {
    const messageIndex = state.messages.findIndex(msg => msg.id === action.payload.messageId);
    if (messageIndex !== -1) {
      state.messages[messageIndex] = {
        ...state.messages[messageIndex],
        ...action.payload.status
      };
    }
  },
  
  // Clear messages when closing conversation
  clearMessages: (state) => {
    state.messages = [];
    state.currentConversation = null;
  },
  
  // Update conversation in list
  updateConversation: (state, action: PayloadAction<Partial<Conversation> & { conversation_id: string }>) => {
    // Update in conversations list
    const index = state.conversations.findIndex(conv => 
      conv.conversation_id === action.payload.conversation_id
    );
    if (index !== -1) {
      state.conversations[index] = { ...state.conversations[index], ...action.payload };
    }

    // Update in message requests list
    const requestIndex = state.messageRequests.findIndex(conv => 
      conv.conversation_id === action.payload.conversation_id
    );
    if (requestIndex !== -1) {
      state.messageRequests[requestIndex] = { ...state.messageRequests[requestIndex], ...action.payload };
    }

    // Update current conversation
    if (state.currentConversation && state.currentConversation.conversation_id === action.payload.conversation_id) {
      state.currentConversation = { ...state.currentConversation, ...action.payload };
    }
  },
  
  clearError: (state) => {
    state.error = null;
  },

  },
  extraReducers: (builder) => {
    builder
      // Fetch conversations
      .addCase(fetchConversations.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchConversations.fulfilled, (state, action) => {
        state.loading = false;
        const { conversations, page } = action.payload;
        
        state.conversationsPage = page;
        state.hasMoreConversations = conversations.length === 20; // Assuming limit is 20

        if (page === 1) {
            state.conversations = conversations;
        } else {
            // Filter out duplicates just in case
            const newConversations = conversations.filter((newConv: Conversation) => 
                !state.conversations.some(existingConv => existingConv.conversation_id === newConv.conversation_id)
            );
            state.conversations = [...state.conversations, ...newConversations];
        }

        // Sync currentConversation if it exists in the fetched conversations
        if (state.currentConversation) {
            const updatedConv = state.conversations.find(c => c.conversation_id === state.currentConversation?.conversation_id);
            if (updatedConv) {
                state.currentConversation = { ...state.currentConversation, ...updatedConv };
            }
        }
      })
      .addCase(fetchConversations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        if (navigator.onLine) {
            toast.error('Failed to load conversations');
        }
      })

      // Fetch message requests
      .addCase(fetchMessageRequests.pending, (state) => {
        state.loadingRequests = true;
        state.error = null;
      })
      .addCase(fetchMessageRequests.fulfilled, (state, action) => {
        state.loadingRequests = false;
        const { requests, page } = action.payload;
        if (page === 1) {
          state.messageRequests = requests;
        } else {
          state.messageRequests = [...state.messageRequests, ...requests];
        }
        
        // Sync currentConversation if it exists in the fetched requests
        if (state.currentConversation) {
            const updatedConv = state.messageRequests.find(r => r.conversation_id === state.currentConversation?.conversation_id);
            if (updatedConv) {
                state.currentConversation = { ...state.currentConversation, ...updatedConv };
            }
        }
      })
      .addCase(fetchMessageRequests.rejected, (state, action) => {
        state.loadingRequests = false;
        state.error = action.payload as string;
      })

      // Handle message request
      .addCase(handleMessageRequest.fulfilled, (state, action) => {
        const { conversationId, action: requestAction } = action.payload;
        if (requestAction === 'accept') {
          // Move from requests to conversations
          const request = state.messageRequests.find(r => r.conversation_id === conversationId);
          if (request) {
            state.conversations.unshift({ ...request, status: 'accepted' });
            state.messageRequests = state.messageRequests.filter(r => r.conversation_id !== conversationId);
          }
        } else {
          // Remove from requests
          state.messageRequests = state.messageRequests.filter(r => r.conversation_id !== conversationId);
        }
      })
      
      // Fetch messages
      .addCase(fetchMessages.pending, (state) => {
        state.loadingMessages = true;
        state.error = null;
      })
      .addCase(fetchMessages.fulfilled, (state, action) => {
        state.loadingMessages = false;
        // console.log(action.payload)
        const { messages, conversation_id, page } = action.payload;
        
        const limit = 50;
        state.hasMore = messages.length === limit;
        state.currentPage = page;

        if (page === 1) {
          state.messages = messages
        } else {
          const newMessages = messages.filter((newMsg: Message) => 
            !state.messages.some(existingMsg => existingMsg.id === newMsg.id)
          );
          state.messages = [...newMessages, ...state.messages];
        }
        
        // Try to find in conversations or requests
        const conversation = state.conversations.find(conv => conv.conversation_id === conversation_id) || 
                             state.messageRequests.find(conv => conv.conversation_id === conversation_id);
        if (conversation) {
          state.currentConversation = conversation;
        }
      })
      .addCase(fetchMessages.rejected, (state, action) => {
        state.loadingMessages = false;
        state.error = action.payload as string;
        if (navigator.onLine) {
            toast.error('Failed to load messages');
        }
      })
      
      // Send message
      .addCase(sendMessage.pending, (state) => {
        state.sendingMessage = true;
        state.error = null;
      })
    //   .addCase(sendMessage.fulfilled, (state, action) => {
    //     state.sendingMessage = false;
    //     // Add the sent message to the messages array
    //     state.messages.push(action.payload);
        
    //     // Update conversation's last message
    //     const conversation = state.conversations.find(
    //       conv => conv.conversation_id === action.payload.conversationId
    //     );
    //     if (conversation) {
    //       conversation.last_message = action.payload.content;
    //       conversation.last_message_at = action.payload.created_at;
    //     }
    //   })
      .addCase(sendMessage.fulfilled, (state, action) => {
        // Add sent message to current messages
        const exists = state.messages.some(msg => msg.id === action.payload.id);
        if (!exists) {
          state.messages.push(action.payload);
        }
        
        // Update conversation's last message and move to top
        const conversationIndex = state.conversations.findIndex(
          conv => conv.conversation_id === action.payload.conversation_id
        );
        
        if (conversationIndex !== -1) {
          const updatedConversation = {
            ...state.conversations[conversationIndex],
            last_message: action.payload.content,
            last_message_at: action.payload.created_at,
          };
          
          // Remove from current position and add to top
          state.conversations.splice(conversationIndex, 1);
          state.conversations.unshift(updatedConversation);
        }
      })
      .addCase(sendMessage.rejected, (state, action) => {
        state.sendingMessage = false;
        state.error = action.payload as string;
        // toast.error('Failed to send message');
      })
      
      // Mark as read
      .addCase(markAsRead.fulfilled, (state, action) => {
        const conversationId = action.payload;
        // Mark all messages in this conversation as read
        state.messages.forEach(message => {
          if (message.conversation_id === conversationId) {
            message.is_read = true;
          }
        });
        // Decrement unread count? Ideally fetch fresh count.
        const conversation = state.conversations.find(c => c.conversation_id === conversationId);
        if (conversation) {
          if (conversation.unread_count) {
            state.unreadCount = Math.max(0, state.unreadCount - conversation.unread_count);
          }
          conversation.unread_count = 0;
        }
      })
      .addCase(markAsRead.rejected, (state, action) => {
        state.error = action.payload as string;
        if (navigator.onLine) {
            toast.error('Failed to mark messages as read');
        }
      })

      // Fetch unread count
      .addCase(fetchUnreadMessageCount.fulfilled, (state, action) => {
        // @ts-ignore
        state.unreadCount = action.payload || 0;
      })

      // Delete conversation
      .addCase(deleteConversation.fulfilled, (state, action) => {
        const deletedId = action.payload;
        state.conversations = state.conversations.filter(c => c.conversation_id !== deletedId);
        if (state.currentConversation?.conversation_id === deletedId) {
          state.currentConversation = null;
          state.messages = [];
        }
      });
  },
});

export const { 
  setCurrentConversation, 
  clearCurrentConversation, 
  addMessage, 
  updateMessageStatus, 
  clearError,
  clearMessages,
  updateConversation
} = messagesSlice.actions;

export default messagesSlice.reducer;
